import * as React from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useState } from "react";
import {
  ITransferListItem,
  ITransferListPanel,
} from "../../Typings/TransferList";
import ListSkeleton from "../Skeleton/ListSkeleton";
import { AddCircleOutline } from "@mui/icons-material";
import { ListItemButton } from "@mui/material";

function not(a: readonly ITransferListItem[], b: readonly ITransferListItem[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(
  a: readonly ITransferListItem[],
  b: readonly ITransferListItem[]
) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(
  a: readonly ITransferListItem[],
  b: readonly ITransferListItem[]
) {
  return [...a, ...not(b, a)];
}

interface ITransferListProps {
  rightPanel: ITransferListPanel;
  leftPanel: ITransferListPanel;
}

export default function TransferList({
  rightPanel,
  leftPanel,
}: ITransferListProps) {
  const [checked, setChecked] = useState<ITransferListItem[]>([]);

  var leftListItems = leftPanel.listItems;
  var rightListItems = rightPanel.listItems;

  const leftChecked = intersection(checked, leftListItems);
  const rightChecked = intersection(checked, rightListItems);

  const handleToggle = (value: ITransferListItem) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: readonly ITransferListItem[]) =>
    intersection(checked, items).length;

  const handleToggleAll = (items: readonly ITransferListItem[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    rightPanel.setListItems(rightListItems.concat(leftChecked));
    leftPanel.setListItems(not(leftListItems, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    leftPanel.setListItems(leftListItems.concat(rightChecked));
    rightPanel.setListItems(not(rightListItems, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customListPanel = (panel: ITransferListPanel) => (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        sx={{ backgroundColor: "#f0f0f0", px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(panel.listItems)}
            checked={
              numberOfChecked(panel.listItems) === panel.listItems.length &&
              panel.listItems.length !== 0
            }
            indeterminate={
              numberOfChecked(panel.listItems) !== panel.listItems.length &&
              numberOfChecked(panel.listItems) !== 0
            }
            disabled={panel.listItems.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={panel.title}
        subheader={`${numberOfChecked(panel.listItems)}/${
          panel.listItems.length
        } selected`}
      />
      <Divider />
      {!panel.listIsLoading ? (
        <List
          sx={{
            width: "100%",
            height: "40vh",
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          dense
          component="div"
          role="list"
        >
          {panel.listItems.map((value: ITransferListItem) => {
            var labelSlug = value.label.replace(/\s/g, "-").toLocaleLowerCase();
            var index = panel.listItems.indexOf(value);
            const labelId = `transfer-list-all-item-${labelSlug}-${index}-label`;

            return (
              <ListItemButton
                key={`${value.label}-${index}`}
                role="listitem"
                onClick={handleToggle(value)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(value) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      "aria-labelledby": labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.label} />
              </ListItemButton>
            );
          })}
          {panel.listHasAddItem ? (
            <ListItem role="listitem" onClick={panel.addItemFunction}>
              <ListItemText
                sx={{ textAlign: "right", mr: "2%", cursor: "pointer" }}
                id={"left-plus"}
                primary={panel.addItemLabel}
              />
              <ListItemIcon sx={{ cursor: "pointer" }}>
                <AddCircleOutline />
              </ListItemIcon>
            </ListItem>
          ) : null}
        </List>
      ) : (
        <List
          sx={{
            width: "100%",
            height: "40vh",
            bgcolor: "background.paper",
            overflow: "auto",
          }}
          dense
          component="div"
          role="list"
        >
          <ListSkeleton numRows={6} />
        </List>
      )}
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={5.5}>
        {customListPanel(leftPanel)}
      </Grid>
      <Grid item xs={1}>
        <Grid container direction="column" alignItems="top" alignSelf="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={5.5}>
        {customListPanel(rightPanel)}
      </Grid>
    </Grid>
  );
}
